export type Layer = {
  name: string;
  detail: string;
  icon: "experience" | "capability" | "orchestration" | "system";
};

export type Principle = {
  title: string;
  text: string;
};

export type TeamRole = {
  role: string;
  focus: string;
  description: string;
};

export type RolloutPhase = {
  name: string;
  detail: string;
  icon: "catalog" | "selfservice" | "insights" | "goldenpaths";
  // When the capability is introduced, in months from kick-off (0 = now).
  startMonth: number;
};

// Four-layered model for delivering a consistent Developer Experience.
export const platformLayers: Layer[] = [
  {
    name: "Developer Experience layer",
    detail:
      "The day-to-day interface for developers. Declarative and as code — with a portal for accessibility, insights and dashboards.",
    icon: "experience",
  },
  {
    name: "Capability layer",
    detail: "Software supply chain capabilities defined by the organisation.",
    icon: "capability",
  },
  {
    name: "Automation layer",
    detail:
      "Manages provisioning and reconciliation towards underlying platforms, providing a uniform integration layer.",
    icon: "orchestration",
  },
  {
    name: "Technology platforms layer",
    detail:
      "Infrastructure, tools and services. Resolving the needs of the capability layer.",
    icon: "system",
  },
];

// Incremental rollout — capabilities layered on over roughly the first year.
export const rolloutTimelineMonths = 12;

// Axis markers, in months from kick-off.
export const rolloutMarkers = [
  { month: 0, label: "Now" },
  { month: 3, label: "3 months" },
  { month: 6, label: "6 months" },
  { month: 12, label: "1 year" },
];

export const rolloutPhases: RolloutPhase[] = [
  {
    name: "Software catalog",
    detail:
      "Components, APIs, Systems, docs, ownership, platform resources. Adoption.",
    icon: "catalog",
    startMonth: 0,
  },
  {
    name: "Self-services",
    detail: "Declarative, GitOps, templates, day-2 operations.",
    icon: "selfservice",
    startMonth: 3,
  },
  {
    name: "Actionable insights",
    detail: "Scorecards, health checks, compliance.",
    icon: "insights",
    startMonth: 3,
  },
  {
    name: "Golden paths",
    detail:
      "Opinionated templates wiring the full tech stack and supply chain together.",
    icon: "goldenpaths",
    startMonth: 6,
  },
];

// How we work — reusable principles brought into every engagement.
export const corePrinciples: Principle[] = [
  {
    title: "Platform as a product",
    text: "A platform must earn its place. We treat the IDP as a living product with a roadmap, stakeholders and a real community behind it, rather than a one-off delivery.",
  },
  {
    title: "A common catalog inventory",
    text: "The software catalog is the heart of the IDP. It provides the developer centric view of all software related assets, ownership and dependencies.",
  },
  {
    title: "Embedded, not external",
    text: "We work inside your teams rather than from the outside, finding friction by living it alongside developers who have made this journey before.",
  },
  {
    title: "Golden paths over golden cages",
    text: "Declarative, everything-as-code and shift-left on security and quality, giving teams a composable offering of building blocks that hold regardless of the stack.",
  },
  {
    title: "Compliance by default",
    text: "Short feedback loops consolidate signals from every system into one developer-facing view, so compliance becomes a continuous signal rather than a periodic gate. Surfaced early and cheap, it is one of the strongest reasons teams onboard.",
  },
  {
    title: "Inner sourced",
    text: "A clear contribution model means the platform is built by everyone rather than one central team, so it scales with the organisation.",
  },
];

// The product team we assemble for each engagement.
export const teamRoles: TeamRole[] = [
  {
    role: "DevEx Lead",
    focus: "Developer Experience",
    description:
      "Owns the overall developer experience vision and ensures the platform delivers value across engineering teams.",
  },
  {
    role: "Platform Lead",
    focus: "Platform Engineering",
    description:
      "Drives the technical direction of the platform, owning architecture decisions and cross-layer integration.",
  },
  {
    role: "Platform Developer",
    focus: "Full-stack development",
    description:
      "Builds and maintains the capabilities, workflows, and integrations that make up the platform.",
  },
  {
    role: "DevEx Designer",
    focus: "UX & Developer Experience",
    description:
      "Shapes the interfaces and interactions developers encounter, ensuring clarity, consistency, and low friction.",
  },
];

export const discoverySteps: Principle[] = [
  {
    title: "Discover before building",
    text: "Continuously we run structured, time-boxed workshops to find the pain points and bottlenecks. Moving fast in the wrong direction just means arriving at the wrong place sooner. Discovery aligns us on both before investing in features.",
  },
  {
    title: "Developer journey - path to production",
    text: "We interview development teams and map where work gets stuck — the handoffs, the tickets, the manual steps, the cognitive load across the software supply chain.",
  },
  {
    title: "Prototype and validate",
    text: "With insights in place, we create hypotheses and move fast into prototyping, getting early validation from developers and stakeholders before committing to a rolled-out feature.",
  },
];
